<template>
  <div v-if="!isCountryOrStateLanding" class="rhg-map-filters">
    <div v-if="!isMobile" class="rhg-destination-country-city-buttons">
      <button v-if="isCityPage" @click="openModal" class="rhg-filters-button">
        <span class="rhg-filter-button-label">{{ translate('promoFilters') }}</span>
        <i class="rhg-font-icon--filters more-filters"></i>
        <span v-if="hotelsFiltered" class="more-filters__dot"></span>
      </button>
      <TabsDiscovery @selectComponent="selectComponent" :activedSection="selectedTab" />
    </div>
    <div v-else class="rhg-destination-country-city-buttons">
      <button
        v-if="isCityPage"
        @click="openModal"
        class="rhg-filters-button"
        :class="{ 'rhg-filters-button-filters': !isMapView }"
      >
        <div class="rhg-filters-button-container">
          <span class="rhg-filter-button-label">{{ translate('promoFilters') }}</span>
          <i class="rhg-font-icon--filters more-filters"></i>
          <span v-if="hotelsFiltered" class="more-filters__dot"></span>
        </div>
      </button>
      <button
        v-if="!isMapView && isCityPage"
        @click="toggleDropdown"
        class="rhg-filters-button rhg-filters-button-sortby"
      >
        <div class="rhg-filters-button-container">
          <i class="rhg-font-icon--filters more-filters"></i>
          <span class="rhg-filter-button-label">{{ translate('weSortBy') }}</span>
        </div>
      </button>
    </div>
    <TabsDiscovery
      v-if="isMobile"
      @selectComponent="selectComponent"
      :activedSection="selectedTab"
    />
    <div
      v-if="hotelListFiltered.hotels && hotelListFiltered.hotels.length > 0"
      class="rhg-destinations-results-sortby"
    >
      <h2 class="rhg-showing-results">
        <span v-if="numHotels > 0">{{
          translate('promoShowingResults', this.hotels.length, this.hotels.length)
        }}</span>
        <span v-else>{{ translate('weErrorNoResultsFound') }}</span>
      </h2>
      <div v-show="!isMapView && !isMobile && isCityPage" class="rhg-destinations-sortby">
        <span class="rhg-destinations-sortby-label">{{ translate('weSortBy') }}: </span>
        <button @click="toggleDropdown" class="rhg-destinations-sortby-button">
          {{ translate(sortBy) }}
          <i v-if="!showDropdown" class="rhg-font-icon--smallarrow-down"></i>
          <i v-else class="rhg-font-icon--smallarrow-up"></i>
        </button>
      </div>
      <div v-show="showDropdown" class="dropdown" ref="dropdownElement">
        <ul>
          <li @click="sortCards">
            <span id="sortby-distance" :class="{ selected: sortBy === 'desDistance' }">{{
              translate('desDistance')
            }}</span>
          </li>
          <li @click="sortCards">
            <span id="sortby-price-asc" :class="{ selected: sortBy === 'desPriceAsc' }">{{
              translate('desPriceAsc')
            }}</span>
          </li>
          <li @click="sortCards">
            <span id="sortby-price-desc" :class="{ selected: sortBy === 'desPriceDes' }">{{
              translate('desPriceDes')
            }}</span>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="hotels && hotels.length > 0 && isMapView" class="rhg-map-destinations">
      <AzureMap
        v-if="isChina"
        :hotels="hotels"
        @hotelsInView="showHotelMap"
        @onBookNow="onBookNow"
        @openSBCalendar="openSBCalendar"
      />
      <Map
        v-else
        :hotels="hotels"
        @hotelsInView="showHotelMap"
        @onBookNow="onBookNow"
        @openSBCalendar="openSBCalendar"
      />
    </div>
    <FiltersModal
      v-show="isOpenModal"
      :filters="filters"
      :selectedFilters="selectedFilters"
      :numHotels="numHotels"
      :isLoading="isLoading"
      @closeModal="closeModal"
      @toggleExpand="toggleExpand"
      @updatePriceFilter="updatePriceFilter"
      @filterSelected="filterSelected"
      @unselectFilter="unselectFilter"
      @applyFilters="applyFilters"
      @clearFilters="clearFilters"
    />
  </div>
  <div v-else class="rhg-map-filters">
    <div v-if="hotels && hotels.length > 0" class="rhg-destinations-results-sortby">
      <h2 class="rhg-showing-results">
        <span v-if="numHotels > 0">{{
          translate('promoShowingResults', this.hotels.length, this.hotels.length)
        }}</span>
        <span v-else>{{ translate('weErrorNoResultsFound') }}</span>
      </h2>
    </div>
    <div v-if="hotels && hotels.length > 0" class="rhg-map-destinations">
      <AzureMap
        v-if="isChina"
        :hotels="hotels"
        @hotelsInView="showHotelMap"
        @onBookNow="onBookNow"
        @openSBCalendar="openSBCalendar"
      />
      <Map
        v-else
        :hotels="hotels"
        @hotelsInView="showHotelMap"
        @onBookNow="onBookNow"
        @openSBCalendar="openSBCalendar"
      />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex'
import { hotelService, tripAdvisorService } from '@/infrastructure/Factory'
import DestinationHotelList from '@/domain/DestinationHotelList'
import { hotelMixin } from '@/infrastructure/HotelsInMap.js'
import FiltersModal from '@/components/modal/FiltersModal.vue'
import TabsDiscovery from '../components/discovery/TabsDiscovery.vue'
import { media } from '@/infrastructure/Utils'
import { isChina, SearchTypes } from '@/infrastructure/Configuration'

const { mapActions } = createNamespacedHelpers('discovery')

export default {
  name: 'DestinationCountryCity',
  components: {
    Map: () => import('@/components/discovery/map/Map'),
    AzureMap: () => import('@/components/discovery/map/AzureMap'),
    FiltersModal,
    TabsDiscovery,
    ...mapGetters(['getCurrentCustomer, isR4UMember'])
  },
  data: () => ({
    isChina: isChina,
    numHotels: 0,
    isOpenModal: false,
    hotelList: DestinationHotelList.empty(),
    hotelListFiltered: DestinationHotelList.empty(),
    hotelsInMapView: [],
    brandList: [],
    filters: [],
    selectedFilters: [],
    priceRange: { maxPrice: null, minPrice: null },
    selectedTab: 'list',
    showDropdown: false,
    sortBy: 'desDistance',
    isMobile: window.innerWidth < media('mobile-xl'),
    isLoading: false
  }),
  mixins: [hotelMixin],
  mounted() {
    window.addEventListener('resize', this.checkIfMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile)
  },
  methods: {
    ...mapActions(['getDestinationsForCountryOrCity', 'updateHotels']),
    openSBCalendar(hotel) {
      if (hotel && hotel.name && hotel.code) {
        global.selectedHotel = {
          name: hotel.name,
          code: hotel.code
        }
      }
      global.searchBarVue.$emit('open-searchbar-modal')
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth < media('mobile-xl')
    },
    selectComponent(id) {
      if (id === 'list') {
        document.getElementById('rhg-destination-list-hotel-list').style.display = 'flex'
        this.selectedTab = 'list'
      } else {
        document.getElementById('rhg-destination-list-hotel-list').style.display = 'none'
        this.showDropdown = false
        this.selectedTab = 'map'
      }
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
      if (this.showDropdown && !this.isMobile) {
        document.addEventListener('click', this.clickHandler)
      } else {
        document.removeEventListener('click', this.clickHandler)
      }
    },
    clickHandler(event) {
      event.stopPropagation()
      const dropdown = this.$refs.dropdownElement
      const button = document.querySelector('.rhg-destinations-sortby-button')
      const icon = document.querySelector('.rhg-font-icon--smallarrow-up')
      if (
        button &&
        dropdown &&
        !dropdown.contains(event.target) &&
        button !== event.target &&
        icon &&
        icon !== event.target
      ) {
        this.showDropdown = false
      }
    },
    sortCards(event) {
      switch (event.target.id) {
        case 'sortby-distance':
          if (this.sortBy !== 'desDistance') {
            this.sortBy = 'desDistance'
            this.sortByOption(this.sortBy)
          }
          break
        case 'sortby-price-asc':
          if (this.sortBy !== 'desPriceAsc') {
            this.sortBy = 'desPriceAsc'
            this.sortByOption(this.sortBy)
          }
          break
        case 'sortby-price-desc':
          if (this.sortBy !== 'desPriceDes') {
            this.sortBy = 'desPriceDes'
            this.sortByOption(this.sortBy)
          }
          break
      }
      this.showDropdown = !this.showDropdown
    },
    sortByOption(option) {
      if (option === 'desDistance') {
        const hotelsWithPrice = this.hotelListFiltered.hotels
          .filter(hotel => hotel.price > 0)
          .sort((a, b) => a.distance.km - b.distance.km)
        const hotelsWithoutPrice = this.hotelListFiltered.hotels
          .filter(hotel => !(hotel.price > 0))
          .sort((a, b) => a.distance.km - b.distance.km)

        this.hotelListFiltered.hotels = hotelsWithPrice.concat(hotelsWithoutPrice)
      } else if (option === 'desPriceAsc') {
        this.hotelListFiltered.hotels = this.hotelListFiltered.hotels.sort((h1, h2) => {
          if (h1.price === 0 && h2.price !== 0) {
            return 1
          } else if (h1.price !== 0 && h2.price === 0) {
            return -1
          } else if (h1.price === 0 && h2.price === 0) {
            return 0
          } else {
            return h1.price - h2.price
          }
        })
      } else if (option === 'desPriceDes') {
        this.hotelListFiltered.hotels = this.hotelListFiltered.hotels.sort((h1, h2) => {
          if (h1.price === 0 && h2.price !== 0) {
            return 1
          } else if (h1.price !== 0 && h2.price === 0) {
            return -1
          } else if (h1.price === 0 && h2.price === 0) {
            return 0
          } else {
            return h2.price - h1.price
          }
        })
      }
      const hotelCards = document.querySelectorAll('[id^="destination-hotel"]')

      if (hotelCards) {
        this.hotelListFiltered.hotels.forEach(hotel => {
          hotelCards.forEach((hotelCard, index) => {
            if (hotelCard.id.includes(hotel.code)) {
              document.querySelector('.rhg-destination-list-hotel-cards').appendChild(hotelCard)
              hotelCard.classList.remove('rhg-d-none')
              if (index > 7 && !hotelCard.classList.contains('rhg-d-none')) {
                hotelCard.classList.add('rhg-d-none')
              }
            }
          })
        })
        const showMoreButton = document.querySelector('.rhg-destination-list-hotel-list-button')
        if (showMoreButton && hotelCards.length > 9) {
          showMoreButton.classList.remove('rhg-d-none')
        }
      }
    },
    openModal() {
      this.isOpenModal = true
    },
    closeModal() {
      this.isOpenModal = false
    },
    cloneHotelList() {
      this.hotelListFiltered = this.hotelList.clone()
    },
    async fetchHotelsAndAnalytics() {
      try {
        const code = window.cityCode ? window.cityCode : window.countryCode
        await this.getDestinationsForCountryOrCity(code)
        this.numHotels = this.hotels.length
      } catch (e) {
        global.$('#modal-error').modal('show')
      } finally {
        this.$store.dispatch('sendAnalyticsView')
      }
    },
    async fetchPricesByCode(hotelCodes) {
      /* eslint-disable */
      const today = new Date()
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const customer = this.getCurrentCustomer
      const isR4UUser = this.isR4UMember
      const params = {
        hotelCodes: hotelCodes,
        startDate: Dates.format(today, 'YYYY-MM-DD'),
        endDate: Dates.format(tomorrow, 'YYYY-MM-DD'),
        searchType: this.getSearchType,
        memberOnlyRatesHotelCodes: !isR4UUser
          ? this.hotelList.getMORCodes().filter(code => hotelCodes.includes(code))
          : '',
        futureDays: 62,
        origin: ConfigDynamicKey.PRICE_DESTINATIONS.origin,
        adults: 2,
        memberType: customer?.memberTypeExtended?.code || '',
        promotionCode: customer?.companyData?.companyId || ''
      }

      const hotelWithPrices = await hotelService.getMultiplePriceFrom(params)

      return hotelWithPrices
    },
    async fetchAllHotelPrices(batches) {
      const hotelWithPrices = await Promise.all(batches.map(batch => this.fetchPricesByCode(batch)))

      const responseData = {}

      hotelWithPrices.forEach(item => {
        item.hotels.forEach(hotel => {
          const hotelCode = hotel.code
          if (!responseData[hotelCode]) {
            responseData[hotelCode] = hotel
          }
        })
      })
      return Object.values(responseData)
    },
    async fetchPrices() {
      const hotelCodes = this.hotelList.getCodes()
      const maxSize = 25

      if (this.numHotels > maxSize) {
        const batches = hotelCodes.reduce((acc, code, index) => {
          const batchIndex = Math.floor(index / maxSize)
          if (!acc[batchIndex]) {
            acc[batchIndex] = []
          }
          acc[batchIndex].push(code)
          return acc
        }, [])

        await this.fetchAllHotelPrices(batches)
          .then(data => {
            if (data?.length > 0) {
              this.hotelList = this.hotelList.enrichHotelsWithPrice(data)
            }
          })
          .catch(error => console.error('Error fetching prices: ', error))
      } else {
        const hotelWithPrices = await this.fetchPricesByCode(hotelCodes)
        if (hotelWithPrices.hotels && hotelWithPrices.hotels.length > 0) {
          this.hotelList = this.hotelList.enrichHotelsWithPrice(hotelWithPrices.hotels)
        }
      }
    },
    async fetchBrands() {
      const brands = await hotelService.getBrands()

      this.brandList = this.hotelList.getAvailableBrandCodes().map(brand => ({
        code: brand,
        name: brands[brand].name
      }))
    },
    async fetchRatingsByCode(hotelCodes) {
      const tripAdvisorRatings = await tripAdvisorService.getTripAdvisorRating(hotelCodes)
      return tripAdvisorRatings
    },
    async fetchAllHotelRatings(batches) {
      const hotelRatings = await Promise.all(batches.map(batch => this.fetchRatingsByCode(batch)))
      const responseData = {}

      hotelRatings.forEach(item => {
        item.forEach(hotel => {
          const hotelCode = hotel.code
          if (!responseData[hotelCode]) {
            responseData[hotelCode] = hotel
          }
        })
      })
      return Object.values(responseData)
    },
    async fetchRatings() {
      const hotelCodes = this.hotelList.getCodes()
      const maxSize = 20

      if (this.numHotels > maxSize) {
        const batches = hotelCodes.reduce((acc, code, index) => {
          const batchIndex = Math.floor(index / maxSize)
          if (!acc[batchIndex]) {
            acc[batchIndex] = []
          }
          acc[batchIndex].push(code)
          return acc
        }, [])

        await this.fetchAllHotelRatings(batches)
          .then(data => {
            if (data?.length > 0) {
              this.hotelList = this.hotelList.enrichHotelsWithRating(data)
            }
          })
          .catch(error => console.error('Error fetching ratings: ', error))
      } else {
        const tripAdvisorRatings = await this.fetchRatingsByCode(hotelCodes)
        if (tripAdvisorRatings && tripAdvisorRatings.length > 0) {
          this.hotelList = this.hotelList.enrichHotelsWithRating(tripAdvisorRatings)
        }
      }
    },
    createFilters() {
      this.filters = [
        {
          title: 'desHotelTypes',
          id: 'types',
          list: this.hotelList.getAvailableHotelTypeCodes(),
          isSectionExpanded: true
        },
        {
          title: 'desRating',
          id: 'rating',
          list: this.hotelList.getAvailableRatings(),
          isSectionExpanded: false
        },
        {
          title: 'desPrice',
          id: 'price',
          maxRange: Math.max(...this.hotelList.hotels.map(hotel => hotel.price)) || 0,
          minRange: Math.min(...this.hotelList.hotels.map(hotel => hotel.price)) || 0,
          isSectionExpanded: false
        },
        {
          title: 'desAmenities',
          id: 'amenities',
          list: this.hotelList.getAvailableAmenityCodes(),
          isSectionExpanded: false
        },
        {
          title: 'desHotelBrands',
          id: 'brand',
          list: this.brandList,
          isSectionExpanded: false
        }
      ]
    },
    filterSelected(code, id) {
      if (!this.selectedFilters.find(filter => filter.code === code)) {
        this.selectedFilters.push({
          code: code,
          id: id
        })
      }
      if (id === 'rating') this.filterByRatings()
      else this.setHotelListFiltered(code, id)
      this.numHotels = this.hotelListFiltered.hotels.length
    },
    unselectFilter(code) {
      this.selectedFilters = this.selectedFilters.filter(filter => filter.code !== code)
      this.filterByRatings()
      this.filterWithPrice()
      this.numHotels = this.hotelListFiltered.hotels.length
    },
    updatePriceFilter(priceRange) {
      this.priceRange = priceRange
      this.filterByRatings()
      this.filterWithPrice()
      this.numHotels = this.hotelListFiltered.hotels.length
    },
    filterWithPrice() {
      if ((this.priceRange.maxPrice && this.priceRange.minPrice) !== null) {
        this.hotelListFiltered.hotels = this.hotelListFiltered.hotels.filter(
          hotel =>
            hotel.price >= this.priceRange.minPrice && hotel.price <= this.priceRange.maxPrice
        )
      }
    },
    setHotelListFiltered(code, id) {
      this.hotelListFiltered.hotels = this.hotelListFiltered.hotels.filter(hotel => {
        switch (id) {
          case 'amenities':
            return !!hotel[id].find(amenity => amenity.code === code)
          case 'brand':
            return hotel[id].includes(code)
          case 'types':
            return !!hotel[id].find(type => type.code === code)
        }
      })
    },
    filterByRatings() {
      if (
        this.selectedFilters.length > 0 &&
        this.selectedFilters.filter(filter => filter.id === 'rating').length > 0
      ) {
        this.hotelListFiltered = DestinationHotelList.empty()
        this.selectedFilters.forEach(filter => {
          if (filter.id === 'rating') {
            this.hotelListFiltered.hotels = this.hotelListFiltered.hotels.concat(
              this.hotelList.hotels.filter(hotel =>
                hotel[filter.id] !== undefined
                  ? hotel[filter.id].bubbleRating.toString() === filter.code
                  : false
              )
            )
          }
        })
        this.selectedFilters
          .filter(filter => filter.id !== 'rating')
          .forEach(filter => this.setHotelListFiltered(filter.code, filter.id))
      } else {
        this.cloneHotelList()
        this.selectedFilters.forEach(filter => this.setHotelListFiltered(filter.code, filter.id))
      }
    },
    async applyFilters() {
      this.isLoading = true
      const hotelCards = document.querySelectorAll('[id^="destination-hotel"]')
      if (hotelCards && hotelCards.length > 0) {
        hotelCards.forEach(hotelCard => {
          hotelCard.classList.remove('d-none')
          hotelCard.classList.remove('rhg-d-none')
          if (!this.hotelListFiltered.hotels.find(hotel => hotelCard.id.includes(hotel.code)))
            hotelCard.classList.add('d-none')
        })
        const hotelCardsFiltered = Array.from(hotelCards).filter(
          hotelCard => !hotelCard.classList.contains('d-none')
        )
        hotelCardsFiltered.forEach((card, index) => {
          if (index > 7) card.classList.add('rhg-d-none')
        })
        const showMoreButton = document.querySelector('.rhg-destination-list-hotel-list-button')
        if (showMoreButton && hotelCardsFiltered.length < 9) {
          showMoreButton.classList.add('rhg-d-none')
        } else if (showMoreButton && hotelCardsFiltered.length > 9) {
          showMoreButton.classList.remove('rhg-d-none')
        }
      }
      await this.fetchHotelsAndAnalytics()
      await this.updateHotels(
        this.hotels.filter(hotel => this.hotelListFiltered.getCodes().includes(hotel.code))
      )
      this.numHotels = this.hotels.length
      this.closeModal()
      this.isLoading = false
    },
    clearFilters() {
      this.selectedFilters = []
      this.cloneHotelList()
      this.sortByOption(this.sortBy)
      this.numHotels = this.hotelList.hotels.length
    },
    toggleExpand(id) {
      this.filters.forEach(filter => {
        if (filter.id === id) filter.isSectionExpanded = !filter.isSectionExpanded
        else if (filter.isSectionExpanded) filter.isSectionExpanded = !filter.isSectionExpanded
      })
    }
  },
  computed: {
    getSearchType() {
      return this.isR4UMember
        ? this.getCurrentCustomer?.isTravelAgent
          ? SearchTypes.TRAVEL_AGENCY
          : SearchTypes.CORPORATE_ACCOUNT
        : 'lowest'
    },
    hotelsFiltered() {
      return (
        this.selectedFilters.length > 0 ||
        (this.priceRange.maxPrice !==
          Math.max(...this.hotelList.hotels.map(hotel => hotel.price)) &&
          this.priceRange.maxPrice !== null) ||
        (this.priceRange.minPrice !==
          Math.min(...this.hotelList.hotels.map(hotel => hotel.price)) &&
          this.priceRange.minPrice !== null)
      )
    },
    isCityPage() {
      return window?.cityCode
    },
    isMapView() {
      return this.selectedTab === 'map'
    },
    isCountryOrStateLanding() {
      return window?.isCountryOrStateLanding
    }
  },
  async created() {
    await this.fetchHotelsAndAnalytics()

    this.hotelList = DestinationHotelList.fromJSON(window.cityCode, this.hotels || [])
    this.hotelList.hotels = this.hotelList.hotels.sort((h1, h2) => h1.distance.km - h2.distance.km)

    if (this.isCityPage) {
      await this.fetchPrices()
      await this.fetchBrands()
      await this.fetchRatings()

      this.cloneHotelList()
      this.createFilters()
    } else {
      this.cloneHotelList()
      await this.fetchRatings()
    }
  }
}
</script>

<style lang="scss" scoped>
.rhg-map-filters {
  max-width: 1344px;
  margin: auto;
}

.rhg-filters-button {
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 30px;
  color: #242424;
  padding: 9px 20px 9px 20px;
  border-radius: 40px;
  margin-bottom: 20px;
  border: 1px solid #d8d8d8;
  background-color: white;

  &-filters {
    border-radius: 40px 0px 0px 40px;
    border-right: 0px;
  }
  &-sortby {
    border-radius: 0px 40px 40px 0px;
  }

  @media (max-width: 575px) {
    width: 100%;
    justify-content: center;
  }
  &-container {
    display: flex;
    align-items: center;
  }

  .more-filters {
    margin: 0 8px !important;

    &__dot {
      position: relative;
      &::after {
        width: 8px;
        height: 8px;
        position: absolute;
        background-color: red;
        display: inline-block;
        content: '';
        border-radius: 50%;
      }
    }
  }
}

.rhg-separator {
  border: 0.7px solid #d8d8d8;
  opacity: 0.25;
  background-color: #686868;
}

.rhg-showing-results {
  font-size: 24px;
  font-weight: bold;
  line-height: 30px;
  margin: 20px 0px;
}

.rhg-map-destinations {
  height: 700px;
  border-radius: 8px;
  border: 1px solid #d1d1d1;
  overflow: hidden;
}

.rhg-destination-country-city-buttons {
  display: flex;
  width: 100%;
  justify-content: space-between;

  .tabs {
    margin-right: 0px !important;
    @media (max-width: 575px) {
      position: relative;
      top: -70px;
    }
    span {
      text-transform: uppercase;
    }
  }
}

.rhg-destinations-results-sortby {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rhg-destinations-sortby {
  display: flex;
  align-items: center;

  &-label {
    font-size: 16px;
    line-height: 30px;
    color: #242424;
  }

  &-button {
    padding: 9px 12px;
    border-radius: 40px;
    border: 1px solid #d8d8d8;
    font-size: 16px;
    font-weight: 325;
    line-height: 30px;
    color: #242424;
    margin-left: 8px;
    display: flex;
    align-items: center;
    background-color: #fff;

    i::before {
      margin-top: 2px;
      font-size: 20px;
      margin-left: 5px;
      font-weight: 700;
    }
  }
}

.dropdown {
  position: absolute;
  background-color: #ffffff;
  min-width: 300px;
  box-shadow: 2px 2px 10px 10px #00000033;
  border-radius: 10px;
  padding: 12px;
  z-index: 999;
  margin-top: 200px;
  right: 0px;

  @media (max-width: 575px) {
    margin-top: 30px;
    width: 100%;
  }
  li {
    font-size: 16px;
    line-height: 22px;
    margin: 10px 8px;
    span {
      cursor: pointer;
      &.selected {
        font-weight: 700;
      }
    }
  }
}
</style>

<style lang="scss">
.rhg-destination-country-city-buttons {
  .tabs {
    margin-right: 0px !important;
    min-width: fit-content;
    display: flex !important;
    button {
      display: flex !important;
      align-items: center !important;
      padding: 0.5rem 1rem !important;
      i {
        margin-bottom: 2px !important;
      }
    }
    span {
      text-transform: uppercase !important;
      font-size: 12px !important;
      line-height: 16px !important;
    }
  }
}
.tabs {
  position: absolute;
  top: -70px;
  right: 0;
  @media (max-width: 575px) {
    width: fit-content;
    top: -75px;
    right: -15px;
  }
}
.map {
  height: 100% !important;
}
html[dir='rtl'] {
  .tabs {
    right: unset;
    left: 0;
    @media (max-width: 575px) {
      right: unset;
      left: 0;
    }
  }

  .dropdown {
    right: unset;
    left: 0;
    text-align: right;
  }

  .rhg-destinations-sortby-button {
    margin-left: unset;
    margin-right: 8px;
  }
}
</style>
